import React, { useState } from 'react';
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Switch,
  FormControlLabel,
} from '@mui/material';
import HolographicCard from '../../../components/common/HolographicCard.tsx';
import GradientText from '../../../components/common/GradientText.tsx';

interface PreferenceSettingsProps {
  settings: {
    theme: string;
    language: string;
    autoSave: boolean;
    compactView: boolean;
  };
  onUpdate: (type: string, data: any) => Promise<void>;
}

const PreferenceSettings: React.FC<PreferenceSettingsProps> = ({
  settings,
  onUpdate,
}) => {
  const [formData, setFormData] = useState(settings);

  const handleChange = async (key: keyof typeof formData, value: any) => {
    const newData = {
      ...formData,
      [key]: value,
    };
    setFormData(newData);
    await onUpdate('preferences', newData);
  };

  const selectStyles = {
    '& .MuiOutlinedInput-root': {
      color: 'rgba(255, 255, 255, 0.9)',
      '& fieldset': {
        borderColor: 'rgba(138, 43, 226, 0.2)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 255, 255, 0.3)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'rgba(138, 43, 226, 0.5)',
      },
    },
    '& .MuiSelect-icon': {
      color: 'rgba(138, 43, 226, 0.8)',
    },
  };

  const switchStyles = {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#8a2be2',
      '&:hover': {
        backgroundColor: 'rgba(138, 43, 226, 0.1)',
      },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#8a2be2',
    },
  };

  return (
    <HolographicCard>
      <GradientText variant="h6" gradient="primary" gutterBottom>
        Preference Settings
      </GradientText>
      <Stack spacing={3}>
        <FormControl fullWidth>
          <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Theme</InputLabel>
          <Select
            value={formData.theme}
            label="Theme"
            onChange={(e) => handleChange('theme', e.target.value)}
            sx={selectStyles}
          >
            <MenuItem value="light">Light</MenuItem>
            <MenuItem value="dark">Dark</MenuItem>
            <MenuItem value="system">System</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Language</InputLabel>
          <Select
            value={formData.language}
            label="Language"
            onChange={(e) => handleChange('language', e.target.value)}
            sx={selectStyles}
          >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="es">Spanish</MenuItem>
            <MenuItem value="fr">French</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={formData.autoSave}
              onChange={(e) => handleChange('autoSave', e.target.checked)}
              sx={switchStyles}
            />
          }
          label={
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Auto Save
            </Typography>
          }
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.compactView}
              onChange={(e) => handleChange('compactView', e.target.checked)}
              sx={switchStyles}
            />
          }
          label={
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Compact View
            </Typography>
          }
        />
      </Stack>
    </HolographicCard>
  );
};

export default PreferenceSettings; 